import { DatePipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy, inject, computed, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import dayjs from 'dayjs';
import { AddNewWorkingTimeDialogComponent } from 'src/app/components/main/employees/activity/working-times-history/add-new-working-time-dialog/add-new-working-time-dialog.component';
import { CompanyProductEpic } from 'src/app/models/company_product_epic.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatDateRangeInput, MatStartDate, MatEndDate, MatDatepickerToggle, MatDateRangePicker } from '@angular/material/datepicker';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';

export type CompanyProductReportVersion = 'value' | 'hours';

@Component({
  selector: 'app-company-product-report-dialog',
  templateUrl: './company-product-report-dialog.component.html',
  styleUrls: ['./company-product-report-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDatepickerToggle,
    MatSuffix,
    MatDateRangePicker,
    MatHint,
    MatCheckbox,
    MatInput,
    MatSelectModule,
    MatDialogActions,
    MatButton,
    MatIcon,
    MatButtonToggleGroup,
    MatButtonToggle,
  ],
})
export class CompanyProductReportDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<AddNewWorkingTimeDialogComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  private user = inject(UserService);
  protected fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);
  protected utils = inject(UtilsService);

  form: UntypedFormGroup;
  users: User[] = [];

  readonly report_version = signal<CompanyProductReportVersion>('value');
  readonly is_value_version = computed((): boolean => this.report_version() === 'value');
  readonly is_hours_version = computed((): boolean => this.report_version() === 'hours');
  readonly epics = signal<CompanyProductEpic[]>([]);

  constructor() {
    this.form = this.fb.group({
      usersSelected: [''],
      from: [dayjs().subtract(1, 'month').startOf('month').toDate()],
      to: [dayjs().subtract(1, 'month').endOf('month').toDate()],
      company_product_id: [this.data.company_product.id],
      use_time_invested: [false],
      multiplier: [null],
      company_product_epic_ids: [[] as (number | null)[]],
      include_internal_meetings: [true],
    });
  }

  ngOnInit(): void {
    this.fetchWorkers();
    this.fetchEpics();
  }

  onReportVersionChange(version: CompanyProductReportVersion): void {
    this.report_version.set(version);
  }

  fetchEpics() {
    this.api.getCompanyProductEpicsByCompanyProduct(String(this.data.company_product.id)).subscribe(epics => {
      this.epics.set(epics);
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  fetchWorkers() {
    const params: any = { role_id: 1 };
    this.api.getUsers(params).subscribe(users => {
      this.users = users.data;
    });
  }

  onSubmitPdf() {
    let datePipe = new DatePipe('es-ES');
    const epicIds: (number | null)[] = this.form.value['company_product_epic_ids'] ?? [];
    const from = datePipe.transform(this.form.value.from, 'yyyy-MM-dd') as string;
    const to = datePipe.transform(this.form.value.to, 'yyyy-MM-dd') as string;
    const company_product_id = this.form.value['company_product_id'];
    const epic_ids = Array.isArray(epicIds) ? epicIds : [];

    if (this.is_value_version()) {
      this.api.getPartnerReportByValue(company_product_id, from, to, !!this.form.value.include_internal_meetings).subscribe(data => {
        this.utils.downloadPdfFromBlobResponse(data);
      });
      return;
    }

    this.api
      .getWorkingTimeReportBusinessPartner(
        company_product_id,
        from,
        to,
        this.form.value.multiplier,
        this.form.value.use_time_invested,
        'pdf',
        epic_ids
      )
      .subscribe(data => {
        this.utils.downloadPdfFromBlobResponse(data);
      });
  }

  onSubmitCsv() {
    let datePipe = new DatePipe('es-ES');
    const epicIds: (number | null)[] = this.form.value['company_product_epic_ids'] ?? [];
    this.api
      .getWorkingTimeReportBusinessPartner(
        this.form.value['company_product_id'],
        datePipe.transform(this.form.value.from, 'yyyy-MM-dd') as string,
        datePipe.transform(this.form.value.to, 'yyyy-MM-dd') as string,
        this.form.value.multiplier,
        this.form.value.use_time_invested,
        'csv',
        Array.isArray(epicIds) ? epicIds : []
      )
      .subscribe(data => {
        this.utils.downloadCsvFromBlobResponse(data);
      });
  }
}
